// ApiKeyManagerPanel — UI för P-004: issue/list/revoke API-keys.
//
// Flow:
// 1. Enterprise-user klickar "Skapa API-nyckel" → dialog med label-input
//    + scopes + expires-in-days.
// 2. Submit → issueApiKey() → backend returnerar plaintext token EN gång.
// 3. Panel visar "COPY NOW"-banner med token; efter stängning kan token
//    inte läsas igen (hashas med SHA-256 i DB).
// 4. Listan under visar metadata (label, scopes, last_used, status)
//    utan token — med revoke-knapp.

import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Key, Plus, Copy, Check, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import {
  issueApiKey,
  listMyApiKeys,
  revokeApiKey,
  type ApiKeyMetadata,
  type ApiKeyScope,
} from '@/lib/apiKeyClient';
import { usePlan } from '@/hooks/usePlan';
import { toast } from '@/hooks/use-toast';

const ALL_SCOPES: ApiKeyScope[] = ['runs:trigger', 'runs:read', 'workflows:read'];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('sv-SE', {
    dateStyle: 'short', timeStyle: 'short',
  });
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

export function ApiKeyManagerPanel() {
  const { limits } = usePlan();
  const [keys, setKeys] = useState<ApiKeyMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newToken, setNewToken] = useState<{ token: string; label: string } | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await listMyApiKeys();
    if (result.success) setKeys(result.keys);
    setIsLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleRevoke = async (id: string, label: string) => {
    const result = await revokeApiKey(id);
    if (result.success) {
      toast({ title: 'Nyckel revoked', description: label });
      void load();
    } else {
      toast({ title: 'Kunde inte revoka', description: result.error, variant: 'destructive' });
    }
  };

  if (!limits.canRunProcesses) {
    return (
      <div className="p-6 text-center text-xs text-muted-foreground">
        <Key className="w-8 h-8 mx-auto mb-2 opacity-40" />
        API-nycklar kräver Enterprise-plan.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="api-key-manager-panel">
      <div className="flex items-center justify-between p-3 border-b">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Key className="w-4 h-4" />
            API-nycklar
          </h3>
          <p className="text-xs text-muted-foreground">
            För extern trigger av workflows (Zapier, cron-servers, etc.)
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={isLoading}>
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="api-key-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />
            Ny nyckel
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-2 space-y-2">
        {isLoading && keys.length === 0 && (
          <div className="text-center py-6 text-xs text-muted-foreground">Laddar…</div>
        )}
        {!isLoading && keys.length === 0 && (
          <div className="text-center py-6 text-xs text-muted-foreground">
            Inga nycklar ännu. Klicka "Ny nyckel" för att skapa en.
          </div>
        )}
        {keys.map((key) => {
          const revoked = key.revoked_at !== null;
          const expired = isExpired(key.expires_at);
          const inactive = revoked || expired;
          return (
            <div
              key={key.id}
              className={`p-2 rounded-md border ${inactive ? 'bg-muted/30 opacity-60' : 'bg-card'}`}
              data-testid={`api-key-row-${key.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-xs flex-1 truncate">{key.label}</span>
                {revoked && <Badge variant="destructive" className="text-[9px]">Revoked</Badge>}
                {!revoked && expired && <Badge variant="outline" className="text-[9px]">Utgången</Badge>}
                {!inactive && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Revoke "{key.label}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Alla externa system som använder denna nyckel kommer sluta fungera direkt.
                          Detta går INTE att ångra — du får utfärda en ny nyckel istället.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void handleRevoke(key.id, key.label)}>
                          Ja, revoka
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              <div className="flex flex-wrap gap-1 mb-1">
                {key.scopes.map((s) => (
                  <Badge key={s} variant="outline" className="text-[9px] font-mono">{s}</Badge>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px] text-muted-foreground">
                <div>Skapad: {formatDate(key.created_at)}</div>
                <div>Använd: {formatDate(key.last_used_at)}</div>
                <div>Utgår: {formatDate(key.expires_at)}</div>
                <div>Triggar: {key.scopes.length}x</div>
              </div>
            </div>
          );
        })}
      </div>

      <CreateApiKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(token, label) => {
          setNewToken({ token, label });
          setCreateOpen(false);
          void load();
        }}
      />

      <NewTokenDialog
        open={!!newToken}
        token={newToken?.token ?? ''}
        label={newToken?.label ?? ''}
        onClose={() => setNewToken(null)}
      />
    </div>
  );
}

function CreateApiKeyDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: (token: string, label: string) => void }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<Set<ApiKeyScope>>(new Set(['runs:trigger']));
  const [expiresInDays, setExpiresInDays] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!label.trim()) return;
    setSubmitting(true);
    const result = await issueApiKey(
      label.trim(),
      Array.from(scopes),
      expiresInDays ? parseInt(expiresInDays, 10) : undefined,
    );
    setSubmitting(false);
    if (result.success && result.token) {
      onCreated(result.token, result.label ?? label.trim());
      setLabel('');
      setScopes(new Set(['runs:trigger']));
      setExpiresInDays('');
    } else {
      toast({
        title: 'Kunde inte skapa nyckel',
        description: result.error === 'enterprise-required'
          ? 'Kräver Enterprise-plan'
          : result.error,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skapa ny API-nyckel</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="key-label">Label (bara för dig att komma ihåg)</Label>
            <Input
              id="key-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="t.ex. 'Prod Zapier-integration'"
              maxLength={100}
              data-testid="api-key-label-input"
            />
          </div>
          <div>
            <Label>Scopes (vad nyckeln får göra)</Label>
            <div className="space-y-1 mt-1">
              {ALL_SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox
                    checked={scopes.has(s)}
                    onCheckedChange={(checked) => {
                      const next = new Set(scopes);
                      if (checked) next.add(s); else next.delete(s);
                      setScopes(next);
                    }}
                  />
                  <span className="font-mono">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="key-expires">Utgår om (dagar, valfritt)</Label>
            <Input
              id="key-expires"
              type="number"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              placeholder="Lämna tomt = aldrig"
              min="1"
              max="3650"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Avbryt</Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={!label.trim() || scopes.size === 0 || submitting}
              data-testid="api-key-submit-btn"
            >
              {submitting ? 'Skapar…' : 'Skapa nyckel'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewTokenDialog({
  open, token, label, onClose,
}: { open: boolean; token: string; label: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Ny nyckel skapad: "{label}"
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs">
            <strong>Viktigt:</strong> Detta är ENDA tillfället du ser nyckeln. Kopiera och spara den någonstans säkert.
            Efter stängning kan vi INTE visa den igen — du får skapa en ny nyckel om du tappar bort denna.
          </div>
          <div className="flex gap-2">
            <Input
              readOnly
              value={token}
              className="font-mono text-xs"
              data-testid="api-key-token-display"
            />
            <Button onClick={() => void handleCopy()} data-testid="api-key-copy-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            Använd i Authorization-header: <code className="font-mono bg-muted px-1">Bearer {token.slice(0, 16)}...</code>
          </div>
          <Button onClick={onClose} className="w-full">
            Jag har sparat nyckeln
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
